import React, { useState, useEffect, useCallback } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'
import { logAdminAction } from '../../lib/adminAuth'
import { filterAndRank } from '../../lib/searchRelevance'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface PendingUser {
  id: number
  full_name: string | null
  username: string
  email: string
  phone: string | null
  phone_number: string | null
  country_of_residence: string | null
  created_at: string
}

interface WhatsAppOnboardingProps {
  currentUser?: any
}

export const WhatsAppOnboarding: React.FC<WhatsAppOnboardingProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<PendingUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyUserIds, setBusyUserIds] = useState<Set<number>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  const loadPendingUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/whatsapp-onboarding`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      })
      const result = await response.json()
      if (!response.ok) {
        throw new Error(result.error || result.message || 'Failed to load pending users')
      }
      setUsers(result.users || [])
    } catch (err: any) {
      console.error('WhatsAppOnboarding loadPendingUsers error:', err)
      setError(err.message || 'Failed to load pending users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPendingUsers()
  }, [loadPendingUsers])

  const markAsAdded = async (userId: number) => {
    if (busyUserIds.has(userId)) return
    setBusyUserIds(prev => new Set(prev).add(userId))
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'mark_whatsapp_added',
          data: { userId, added: true },
        }),
      })
      const result = await response.json()
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || 'Failed to update WhatsApp status')
      }

      try {
        await logAdminAction(
          currentUser?.adminUser?.email || currentUser?.email,
          'whatsapp_added',
          'user',
          String(userId),
          { whatsapp_added: true }
        )
      } catch (logErr) {
        console.warn('logAdminAction failed (non-fatal):', logErr)
      }

      setUsers(prev => prev.filter(u => u.id !== userId))
    } catch (err: any) {
      console.error('markAsAdded error:', err)
      alert(err.message || 'Failed to mark user as added')
    } finally {
      setBusyUserIds(prev => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
    }
  }

  const markAsRefused = async (user: PendingUser) => {
    if (busyUserIds.has(user.id)) return

    const rawNote = window.prompt(
      `Mark "${user.full_name || user.username}" as REFUSED to join the WhatsApp group.\n\nEnter a reason / note (optional, max 2000 chars):`,
      ''
    )
    // window.prompt returns null when the admin clicks Cancel — abort.
    if (rawNote === null) return

    const note = rawNote.trim().slice(0, 2000)

    setBusyUserIds(prev => new Set(prev).add(user.id))
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/user-management`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          operation: 'mark_whatsapp_refused',
          data: { userId: user.id, refused: true, note },
        }),
      })
      const result = await response.json()
      if (!response.ok || result?.success === false) {
        throw new Error(result?.error || 'Failed to update WhatsApp refusal status')
      }

      try {
        await logAdminAction(
          currentUser?.adminUser?.email || currentUser?.email,
          'whatsapp_refused',
          'user',
          String(user.id),
          { whatsapp_refused: true, note: note || null }
        )
      } catch (logErr) {
        console.warn('logAdminAction failed (non-fatal):', logErr)
      }

      setUsers(prev => prev.filter(u => u.id !== user.id))
    } catch (err: any) {
      console.error('markAsRefused error:', err)
      alert(err.message || 'Failed to mark user as refused')
    } finally {
      setBusyUserIds(prev => {
        const next = new Set(prev)
        next.delete(user.id)
        return next
      })
    }
  }

  const filteredUsers = filterAndRank(
    users,
    searchTerm,
    (u) => ({
      primary: [u.full_name, u.username, u.email],
      peripheral: [u.phone, u.phone_number],
    }),
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-yellow-400">📱 WhatsApp Onboarding</h2>
          <p className="text-white mt-1">
            Track which active shareholders still need to be added to the official WhatsApp group.
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-white">Pending: {users.length}</div>
          <button
            onClick={loadPendingUsers}
            className="px-4 py-2 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
            disabled={loading}
          >
            {loading ? 'Refreshing…' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <div className="glass-card p-4 border border-gray-700/50">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name, username, email, or phone…"
          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
        />
      </div>

      {error && (
        <div className="glass-card p-4 border border-red-500/40 bg-red-500/10 text-red-300">
          {error}
        </div>
      )}

      <div className="glass-card border border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60 text-gray-300 uppercase tracking-wider text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Full Name</th>
                <th className="px-4 py-3 text-left">Username</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-white divide-y divide-gray-800">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    Loading pending shareholders…
                  </td>
                </tr>
              )}
              {!loading && filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    {users.length === 0
                      ? '🎉 No pending shareholders — every active shareholder is in the WhatsApp group.'
                      : 'No users match your search.'}
                  </td>
                </tr>
              )}
              {!loading && filteredUsers.map(u => {
                const phone = u.phone || u.phone_number || '—'
                const fullName = u.full_name || '—'
                const isBusy = busyUserIds.has(u.id)
                return (
                  <tr key={u.id} className="hover:bg-gray-900/40">
                    <td className="px-4 py-3 align-top">{fullName}</td>
                    <td className="px-4 py-3 align-top text-yellow-300">{u.username}</td>
                    <td className="px-4 py-3 align-top">{phone}</td>
                    <td className="px-4 py-3 align-top break-all">{u.email}</td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                        <button
                          onClick={() => markAsAdded(u.id)}
                          disabled={isBusy}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isBusy ? 'Updating…' : '✅ Mark as Added'}
                        </button>
                        <button
                          onClick={() => markAsRefused(u)}
                          disabled={isBusy}
                          className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isBusy ? '…' : '🚫 Mark as Refused'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default WhatsAppOnboarding
